import { useEffect, useRef } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Scaling } from "lucide-react";
import { useDesign } from "../../lib/store";
import { formatLength } from "../../lib/geom";
import { nudgedPose, type NudgeDirection } from "../../lib/furniture/nudge";
import { nudgeStep } from "../../lib/furniture/transform";
import { noteAdjustChange } from "../../lib/transformTelemetry";
import { track } from "../../lib/telemetry";
import type { Furniture, Unit } from "../../lib/types";

/** Hold an arrow this long before it starts repeating, then repeat at this rate. */
const REPEAT_DELAY_MS = 400;
const REPEAT_EVERY_MS = 90;

const ARROWS: Array<{ dir: NudgeDirection; label: string; Icon: typeof ArrowUp }> = [
  { dir: "left", label: "Move left", Icon: ArrowLeft },
  { dir: "up", label: "Move up", Icon: ArrowUp },
  { dir: "down", label: "Move down", Icon: ArrowDown },
  { dir: "right", label: "Move right", Icon: ArrowRight },
];

/**
 * The selection bar while a piece is in Adjust mode: what is being adjusted
 * and its live size (the finger never covers it), four nudge arrows (a tap
 * path for moving, one grid step each, held to repeat) and Done.
 */
export function AdjustModeBar({
  piece,
  unit,
  onDone,
}: {
  piece: Furniture;
  unit: Unit;
  onDone: () => void;
}) {
  const repeat = useRef<{ delay: ReturnType<typeof setTimeout> | null; every: ReturnType<typeof setInterval> | null }>({
    delay: null,
    every: null,
  });

  function stopRepeat() {
    if (repeat.current.delay) clearTimeout(repeat.current.delay);
    if (repeat.current.every) clearInterval(repeat.current.every);
    repeat.current = { delay: null, every: null };
  }
  useEffect(() => stopRepeat, []);

  function nudge(direction: NudgeDirection) {
    const s = useDesign.getState();
    const item = s.design.furniture.find((f) => f.id === piece.id);
    if (!item) return;
    const step = nudgeStep(s.snapEnabled ? s.gridSnapInches : 0);
    const next = nudgedPose(item, direction, step, s.design.room);
    if (next.x === item.x && next.y === item.y) return;
    s.coalesceEdit(() => s.updateFurniture(item.id, { x: next.x, y: next.y }));
    track("furniture_nudged", { direction });
    noteAdjustChange();
  }

  const rotation = Math.round(piece.rotation) % 360;
  const size = `${formatLength(piece.width, unit)} × ${formatLength(piece.depth, unit)}${
    rotation ? ` · ${rotation}°` : ""
  }`;

  return (
    <div
      role="toolbar"
      aria-label={`Adjusting ${piece.name || "piece"}`}
      className="flex h-14 items-center gap-0.5 rounded-2xl bg-white p-1.5 ring-2 ring-accent-500 shadow-lg shadow-accent-900/20"
      data-testid="adjust-bar"
    >
      <div className="min-w-0 flex-1 pl-2">
        <div className="truncate text-body-sm font-semibold leading-tight text-ink-900">
          {piece.name || "Unnamed piece"}
        </div>
        <div className="flex min-w-0 items-center gap-1 text-xs leading-tight tabular-nums text-ink-500">
          <Scaling className="size-3 shrink-0 text-accent-600" aria-hidden />
          <span className="sr-only">Adjusting</span>
          <span className="truncate" data-testid="adjust-size">
            {size}
          </span>
        </div>
      </div>
      {ARROWS.map(({ dir, label, Icon }) => (
        <button
          key={dir}
          type="button"
          aria-label={label}
          data-testid={`nudge-${dir}`}
          className="inline-flex h-11 w-10 shrink-0 touch-none items-center justify-center rounded-xl text-ink-700 active:bg-accent-50 active:text-accent-700 transition-colors"
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            stopRepeat();
            nudge(dir);
            repeat.current.delay = setTimeout(() => {
              repeat.current.every = setInterval(() => nudge(dir), REPEAT_EVERY_MS);
            }, REPEAT_DELAY_MS);
          }}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          onClick={(e) => {
            // Pointer presses already nudged; this is keyboard activation.
            if (e.detail === 0) nudge(dir);
          }}
        >
          <Icon className="size-[18px]" />
        </button>
      ))}
      <button
        type="button"
        onClick={onDone}
        className="ml-0.5 inline-flex h-11 shrink-0 items-center rounded-xl bg-accent-600 px-3.5 text-body-sm font-semibold text-white active:bg-accent-700 transition-colors"
        data-testid="adjust-done"
      >
        Done
      </button>
    </div>
  );
}
